/**
 * Lazy Loading System for MontRoyal Jewelry
 * Loads non-critical JavaScript on demand to improve initial page load performance
 */

window.WAU = window.WAU || {};

WAU.LazyLoader = {
  loaded: new Set(),
  loading: new Map(),

  /**
   * Load a script dynamically
   * @param {string} url - URL or asset name
   * @param {object} options - Loading options
   * @returns {Promise}
   */
  loadScript(url, options = {}) {
    const {
      defer = true,
      async = false,
      onLoad = null,
      onError = null
    } = options;

    // Normalize URL
    const scriptUrl = url.includes('://') ? url : `${window.Shopify?.routes?.root || '/'}assets/${url}`;

    // Return cached promise if already loading
    if (this.loading.has(scriptUrl)) {
      return this.loading.get(scriptUrl);
    }

    // Return resolved promise if already loaded
    if (this.loaded.has(scriptUrl)) {
      return Promise.resolve();
    }

    const loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.defer = defer;
      script.async = async;

      script.onload = () => {
        this.loaded.add(scriptUrl);
        this.loading.delete(scriptUrl);
        if (onLoad) onLoad();
        resolve();
      };

      script.onerror = (error) => {
        this.loading.delete(scriptUrl);
        if (onError) onError(error);
        reject(new Error(`Failed to load script: ${scriptUrl}`));
      };

      document.head.appendChild(script);
    });

    this.loading.set(scriptUrl, loadPromise);
    return loadPromise;
  },

  /**
   * Load Flickity carousel library on demand
   */
  loadFlickity() {
    return this.loadScript('vendor-flickity.js');
  },

  /**
   * Load Zoom library on demand
   */
  loadZoom() {
    return this.loadScript('vendor-zoom.js');
  },

  /**
   * Load Parallax library on demand
   */
  loadParallax() {
    return this.loadScript('vendor-parallax.js');
  },

  /**
   * Initialize lazy loading for carousels
   */
  initCarouselLazyLoad() {
    // Use Intersection Observer to load Flickity when carousel enters viewport
    if ('IntersectionObserver' in window) {
      const carousels = document.querySelectorAll('[data-lazy-carousel]');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadFlickity().then(() => {
              // Carousel init code goes here
              const event = new CustomEvent('flickity:loaded');
              entry.target.dispatchEvent(event);
            }).catch(err => {
              if (WAU.error) WAU.error('Failed to load Flickity:', err);
            });
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px'
      });

      carousels.forEach(carousel => observer.observe(carousel));
    } else {
      // Fallback for browsers without Intersection Observer
      this.loadFlickity();
    }
  },

  /**
   * Initialize lazy loading for zoom
   */
  initZoomLazyLoad() {
    const zoomElements = document.querySelectorAll('[data-lazy-zoom]');

    zoomElements.forEach(element => {
      element.addEventListener('mouseenter', () => {
        this.loadZoom().catch(err => {
          if (WAU.error) WAU.error('Failed to load Zoom:', err);
        });
      }, { once: true });
    });
  },

  /**
   * Initialize all lazy loaders
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initCarouselLazyLoad();
        this.initZoomLazyLoad();
      });
    } else {
      this.initCarouselLazyLoad();
      this.initZoomLazyLoad();
    }
  }
};

/**
 * A11y shim — mirror aria-hidden ↔ inert
 *
 * Lighthouse audit "[aria-hidden='true'] elements contain focusable
 * descendents" wants tab focus removed from hidden subtrees. The modern
 * fix is the `inert` attribute.
 *
 * IMPORTANT: Flickity (and similar carousels) sets aria-hidden="true" on
 * cells that are visually outside the active position — INCLUDING cells
 * still visible in multi-cell carousels. Applying `inert` to those would
 * BLOCK CLICKS on visible product cards. So we explicitly exclude any
 * element inside a Flickity viewport.
 */
WAU.AriaInertShim = {
  observer: null,

  shouldSkip(el) {
    if (!el || !el.closest) return true;
    // Skip anything inside a Flickity carousel — Flickity manages aria-hidden
    // on visible slides which would break clicks if marked inert.
    if (el.closest('.flickity-viewport, .flickity-slider, .flickity-enabled')) return true;
    // Skip product loops (use Flickity)
    if (el.classList && el.classList.contains('product-loop__item')) return true;
    return false;
  },

  syncElement(el) {
    if (!el || el.nodeType !== 1) return;
    if (this.shouldSkip(el)) {
      // Make sure we don't leave a stale inert from a previous (incorrect) run
      if (el.hasAttribute('inert')) el.removeAttribute('inert');
      return;
    }
    const ariaHidden = el.getAttribute('aria-hidden') === 'true';
    if (ariaHidden && !el.hasAttribute('inert')) {
      el.setAttribute('inert', '');
    } else if (!ariaHidden && el.hasAttribute('inert')) {
      el.removeAttribute('inert');
    }
  },

  init() {
    if (typeof MutationObserver === 'undefined') return;

    // Cleanup any stray `inert` attributes from a previous version of this
    // shim that may have run before this update reached the user.
    document.querySelectorAll('[inert]').forEach(el => {
      if (this.shouldSkip(el)) el.removeAttribute('inert');
    });

    // Sync existing elements on first run
    document.querySelectorAll('[aria-hidden="true"]').forEach(el => this.syncElement(el));

    this.observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
          this.syncElement(m.target);
        } else if (m.type === 'childList') {
          m.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              if (node.matches && node.matches('[aria-hidden="true"]')) this.syncElement(node);
              if (node.querySelectorAll) {
                node.querySelectorAll('[aria-hidden="true"]').forEach(el => this.syncElement(el));
              }
            }
          });
        }
      }
    });

    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });
  }
};

// Auto-initialize
WAU.LazyLoader.init();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => WAU.AriaInertShim.init());
} else {
  WAU.AriaInertShim.init();
}
